const SAVED_PRODUCTS_KEY = "NUUN_RECENTLY_VIEWED_PRODUCTS";

/**
 * @typedef {Object} ViewedProductInfo
 * @property {number} productId - The ID of the product.
 * @property {number} variantId - The ID of the variant.
 * @property {string} handle - The handle of the product.
 */

/**
 * @returns {Array<ViewedProductInfo>}
 */
function getViewedProductsFromStorage() {
  const data = localStorage.getItem(SAVED_PRODUCTS_KEY) || "[]";
  try {
    const products = JSON.parse(data);

    // Validate the data
    if (Array.isArray(products)) {
      for (const p of products) {
        if (!p.productId || !p.variantId || !p.handle) {
          console.error("Invalid saved product", p);
          return [];
        }
      }
    }
    return products;
  } catch (e) {
    console.error("Could not parse viewed products", e);
    return [];
  }
}

/**
 * @param {Array<ViewedProductInfo>} data
 */
function setViewedProductsToStorage(data) {
  localStorage.setItem(SAVED_PRODUCTS_KEY, JSON.stringify(data));
}

/**
 * @param {number} productId
 * @param {number} variantId
 * @param {string} handle
 */
async function saveViewedProduct(productId, variantId, handle) {
  const allowed = await isPreferencesAllowed();

  if (allowed === false) {
    return;
  }

  let data = getViewedProductsFromStorage();

  // Remove any existing products, since we only keep 1 product saved (even if different variants)
  data = data.filter((p) => p.productId !== productId);

  // Prepend as most recently viewed
  data.unshift({ productId, variantId, handle });

  // Trim to 4 items (if needed), so only render 3 but we need 4 so we can skip the currently
  // viewed product.
  data.length = Math.min(data.length, 4);

  setViewedProductsToStorage(data);
}

/**
 * @param {number} productId
 * @returns {Array<ViewedProductInfo>}
 */
function getRecentlyViewedProducts(productId) {
  const data = getViewedProductsFromStorage();
  return data.filter((p) => p.productId !== productId);
}

/**
 * @returns {Promise<boolean>}
 */
async function isPreferencesAllowed() {
  if (window.Shopify.customerPrivacy) {
    return window.Shopify.customerPrivacy.preferencesProcessingAllowed();
  }

  return new Promise((resolve, reject) => {
    window.Shopify.loadFeatures(
      [
        {
          name: "consent-tracking-api",
          version: "0.1",
        },
      ],
      (error) => {
        if (error) {
          console.error("Error loading consent tracking API", error);
          return reject(error);
        }
        const allowed =
          window.Shopify.customerPrivacy.preferencesProcessingAllowed();
        resolve(allowed);
      }
    );
  });
}

document.addEventListener("visitorConsentCollected", (event) => {
  if (event.detail.preferencesAllowed === false) {
    localStorage.removeItem(SAVED_PRODUCTS_KEY);
  }
});

export { getRecentlyViewedProducts as g, isPreferencesAllowed as i, saveViewedProduct as s };
